import {Component, inject, OnInit, ViewChild} from "@angular/core";
import ILoginPageViewData = NCVIB.app.login.ViewData.ILoginPageViewData;
import IAuthenticationCommand = NCVIB.app.login.IAuthenticationCommand;
import ILoginPageLanguageViewData = NCVIB.app.login.ViewData.ILoginPageLanguageViewData;
import {CoreService} from "../common/core.service";
import {firstValueFrom} from "rxjs";
import {FormsModule, NgModel} from "@angular/forms";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {CommonModule} from "@angular/common";
import {PublicOtpComponent} from "./OTP/otp.component";
import {NgbDropdown} from "@ng-bootstrap/ng-bootstrap";
import {CheckboxComponent} from "../common/CheckBox/checkbox.component";
import {RouterLink} from "@angular/router";
import {environment} from "../../environments/environment";
import {ReloadService} from "../reload.service";
import {AuthService} from "../auth/auth-service";
import {ProjectIdService} from "../common/project-id.service";
import {PubSubService} from "../common/pub-sub.service";
import {NewVersionCheckerService} from "../Main/AppVersionHandling/version-checker.service";
import {BroadcastService} from "../common/broadcast.service";
import ILoginVerification = NCVIB.app.login.ViewData.ILoginVerification;
import IRedirectViewData = NCVIB.app.login.ViewData.IRedirectViewData;
import {LocalizationService} from "../common/localization/localization.service";

export enum LoginStatus {
    Unknown = 0,
    SignedIn = 1,
    SignedOut = 2
}

@Component({
    templateUrl: './login.component.html',
    imports: [
        FormsModule,
        CommonModule,
        TranslateModule,
        PublicOtpComponent,
        NgbDropdown,
        CheckboxComponent,
        RouterLink,
    ],
    standalone:true,
    styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
    userName: string = "";
    password: string = "";
    errorMessage: string = "";
    rememberMe: boolean = false;
    selectedLanguage: ILoginPageLanguageViewData | undefined;
    languages: ILoginPageLanguageViewData[] = [];
    userViewData: ILoginPageViewData | undefined;
    requires2Fa: boolean = false;
    hidePassword: boolean = true;

    @ViewChild("inputUsername") inputUsername: NgModel | undefined;

    private broadcast: BroadcastService = inject(BroadcastService);
    private core: CoreService = inject(CoreService);
    private pubSub: PubSubService = inject(PubSubService);
    private authService: AuthService = inject(AuthService);
    private projectIdService: ProjectIdService = inject(ProjectIdService);
    private reloadService: ReloadService = inject(ReloadService);
    private translateService: TranslateService = inject(TranslateService);
    private pubSubService: PubSubService = inject(PubSubService);
    private newVersionCheckerService: NewVersionCheckerService = inject(NewVersionCheckerService);
    private localizationService: LocalizationService = inject(LocalizationService);
    private isLoggingIn: boolean = false;

    constructor() {
        this.pubSubService.subscribe("NewVersion", async (dummy: boolean) => {
            await this.newVersionCheckerService.activateNewVersion(true);
        })

        this.pubSub.subscribe("user-log-in", () => {
            if (!this.isLoggingIn) {
                if (!this.goToPreviousUrl()) {
                    this.goToStartPage().then();
                }
            }
        });

        this.pubSub.subscribe("logon-user-changed", () => {
            sessionStorage.removeItem("log-on-path");
        });
    }

    public loginStatus: LoginStatus = LoginStatus.Unknown;

    public isSignedIn(): boolean {
        return this.loginStatus == LoginStatus.SignedIn;
    }

    public isSignedOut(): boolean {
        return this.loginStatus == LoginStatus.SignedOut;
    }

    async ngOnInit() {
        const isSignedIn: boolean | undefined = await firstValueFrom(this.authService.isSignedIn());
        this.loginStatus = isSignedIn ? LoginStatus.SignedIn : LoginStatus.SignedOut;

        await this.checkServerAvailability();

        if (this.isSignedIn()) {
            if (!this.goToPreviousUrl()) {
                this.goToStartPage().then();
            }
        }
    }

    public async goToStartPage() {
        let projectId: number | null | string = localStorage.getItem('projectId_' + this.authService.username);

        if (!projectId) {
            await this.projectIdService.initialize();
            projectId = this.projectIdService.projectId;
        }
        this.reloadService.setPath(`${projectId}/Project/Map`);
        location.reload();
    }

    public async login() {
        let serverAvailable = await this.ping();

        if (!serverAvailable) {
            alert("Server is not responding at the moment.");
            return;
        }

        const command: IAuthenticationCommand = this.createAuthenticationCommand();

        this.verifyLogin(command)
            .then((requires2Fa) => {
                if (requires2Fa) {
                    this.requires2Fa = true;
                } else {
                    this.actualLogin(command);
                }
            })
            .catch(() => console.log("Login rejected"));
    }

    public signOut() {
        this.authService.signOut();
    }

    public clearErrorMessage() {
        if (this.errorMessage) {
            this.errorMessage = "";
        }
    }

    private createAuthenticationCommand(requires2Fa: boolean = false, verificationCode: string = ""): IAuthenticationCommand {
        return {
            username: this.userName,
            password: this.password,
            language: this.selectedLanguage != undefined ? this.selectedLanguage : this.languages[1],
            rememberMe: this.rememberMe,
            requires2Fa: requires2Fa,
            verificationCode: verificationCode
        };
    }

    private verifyLogin(command: IAuthenticationCommand) {
        return new Promise((resolve, reject) => {
            this.core.post<ILoginVerification>(`api/auth/user/verify-login`,
                command,
                "Verify Login",
                true,
                (result) => {
                })
                .subscribe(response => {
                    if (response.isVerified) {
                        resolve(response.requires2Fa);
                    } else {
                        if (response.errorMessage) {
                            this.errorMessage = response.errorMessage;
                        }
                        reject();
                    }
                });
        });
    }

    private actualLogin(command: IAuthenticationCommand) {
        this.core.post<IRedirectViewData>(`api/auth/user/login`,
            command,
            "Login",
            true,
            (result) => {
            })
            .subscribe(async response => {
                if (response.errorMessage) {
                    this.errorMessage = response.errorMessage;
                } else {
                    let previousUser = localStorage.getItem("current-user");
                    localStorage.setItem("current-user", command.username!);

                    if (previousUser != command.username!) {
                        sessionStorage.removeItem("log-on-path");
                        this.broadcast.send("logon-user-changed");
                    }

                    this.isLoggingIn = true;
                    this.broadcast.send("user-log-in");
                    if (!this.goToPreviousUrl()) {
                        this.reloadService.setPath(`${response.projectId}/Project/Map`);
                        location.reload();
                    }
                }
            });
    }

    private loadUserViewData() {
        this.core.get<ILoginPageViewData>(`api/auth/user/load-user-view-data`,
            "Load Login Page",
            true,
            (error) => {
            }).subscribe(viewData => {
            console.log(viewData);
            this.userViewData = viewData;
            this.languages = viewData.languages;
            this.setUserDefaultLanguage();
        });
    }

    private setUserDefaultLanguage() {
        this.selectedLanguage = this.languages.filter(x => x.languageCode.startsWith(this.userViewData?.userLanguage ?? "English"))[0];
        if (!this.selectedLanguage) {
            this.selectedLanguage = this.languages.filter(x => x.name == "English")[0];
            return;
        }
    }

    public onVerificationCodeChanged(code: string) {
        const command: IAuthenticationCommand = this.createAuthenticationCommand();
        command.requires2Fa = true;
        command.verificationCode = code;
        this.actualLogin(command);
    }

    public togglePasswordVisibility() {
        this.hidePassword = !this.hidePassword;
    }

    public languageChanged() {
        let languageCode = this.selectedLanguage?.languageCode!;

        this.localizationService.saveLocaleToLocalStorage(languageCode);
        this.translateService.use(languageCode);
    }

    private async checkServerAvailability() {
        try {
            while (!await this.ping()) {
                await new Promise(resolve => setTimeout(resolve, 2000));
            }

            this.loadUserViewData();
        } catch (error) {
            console.error(error);
        }
    }

    private async ping(): Promise<boolean> {
        try {
            const response = await fetch(environment.apiUrl + 'api/auth/ping', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                return data.status === 'Pong';
            } else {
                return false;
            }
        } catch (error) {
            return false;
        }
    }

    private goToPreviousUrl(): boolean {
        let previousPath = sessionStorage.getItem("log-on-path");
        if (!!previousPath) {
            sessionStorage.removeItem("log-on-path");
            console.log("previousPath", previousPath);
            if (previousPath.indexOf("login") === -1) {
                window.location.href = previousPath;
                return true;
            }
        }

        return false;
    }
}
